// src/components/Header.jsx with added tooltip functionality
import React, { useState } from "react";
import { motion } from "framer-motion";
import ThemeToggle from "./ThemeToggle";

const Header = ({ theme, setTheme, colorTheme, setColorTheme, currentView, setCurrentView, showProfileModal, userProfile }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  
  // Helper function to check if any purchases exist in localStorage
  const purchaseExists = () => {
    const saved = localStorage.getItem("guiltGaugePurchases");
    return saved && JSON.parse(saved).length > 0;
  };
  
  // Toggle mobile menu
  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };
  
  // Close menu when changing views
  const navigateTo = (view) => {
    setCurrentView(view);
    setShowMenu(false);
  };
  
  // Show tooltip
  const showTooltip = (id) => {
    setActiveTooltip(id);
  };
  
  // Hide tooltip
  const hideTooltip = () => {
    setActiveTooltip(null);
  };
  
  return (
    <header className="w-full max-w-md mx-auto mb-4">
      <div className="flex justify-between items-center py-4">
        <motion.div 
          className="flex items-center cursor-pointer"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          onClick={() => navigateTo("input")}
        >
          <img 
            src="/favicons/favicon-32x32.png" // Direct path to favicon
            alt="Favicon" 
            className="mr-2" 
            style={{ width: '41.2px', height: '36px' }}
          />
          <div>
            <h1 className="text-xl font-bold dark:text-white">Guilt Gauge</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Shop first, ask questions later</p>
          </div>
        </motion.div>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-2">
          {/* Navigation buttons */}
          {currentView !== "input" && (
            <div className="relative">
              <motion.button
                onClick={() => navigateTo("input")}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                onMouseEnter={() => showTooltip("add")}
                onMouseLeave={hideTooltip}
              >
                <span role="img" aria-label="add" className="text-xl">➕</span>
              </motion.button>
              {activeTooltip === "add" && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                  Log new purchase
                </div>
              )}
            </div>
          )}
          
          {purchaseExists() && (
            <>
              {currentView !== "dashboard" && (
                <div className="relative">
                  <motion.button
                    onClick={() => navigateTo("dashboard")}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onMouseEnter={() => showTooltip("dashboard")}
                    onMouseLeave={hideTooltip}
                  >
                    <span role="img" aria-label="history" className="text-xl">📊</span>
                  </motion.button>
                  {activeTooltip === "dashboard" && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                      View your dashboard
                    </div>
                  )}
                </div>
              )}
              
              {currentView !== "goals" && (
                <div className="relative">
                  <motion.button
                    onClick={() => navigateTo("goals")}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onMouseEnter={() => showTooltip("goals")}
                    onMouseLeave={hideTooltip}
                  >
                    <span role="img" aria-label="goals" className="text-xl">🎯</span>
                  </motion.button>
                  {activeTooltip === "goals" && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                      Set spending goals
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          
          {/* Shop button - always visible */}
          {currentView !== "shop" && (
            <div className="relative">
              <motion.button
                onClick={() => navigateTo("shop")}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                onMouseEnter={() => showTooltip("shop")}
                onMouseLeave={hideTooltip}
              >
                <span role="img" aria-label="shop" className="text-xl soft-glistening">🛍️</span>
              </motion.button>
              {activeTooltip === "shop" && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                  Mindful shopping picks
                </div>
              )}
            </div>
          )}
          
          {/* Quiz button - new! */}
          {currentView !== "quiz-hub" && (
            <div className="relative">
              <motion.button
                onClick={() => navigateTo("quiz-hub")}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                onMouseEnter={() => showTooltip("quiz")}
                onMouseLeave={hideTooltip}
              >
                <span role="img" aria-label="quiz" className="text-xl">📝</span>
              </motion.button>
              {activeTooltip === "quiz" && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                  Take a quiz
                </div>
              )}
            </div>
          )}
                    
          {/* Combined Theme Toggle (light/dark and color palette) */}
          <div className="relative">
            <ThemeToggle 
              theme={theme} 
              setTheme={setTheme} 
              colorTheme={colorTheme} 
              setColorTheme={setColorTheme} 
              onMouseEnter={() => showTooltip("theme")}
              onMouseLeave={hideTooltip}
            />
            {activeTooltip === "theme" && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                Change theme
              </div>
            )}
          </div>
          
          {/* User profile button */}
          <div className="relative">
            <motion.button
              onClick={showProfileModal}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onMouseEnter={() => showTooltip("profile")}
              onMouseLeave={hideTooltip}
            >
              <span role="img" aria-label="profile" className="text-xl">
                {userProfile ? "👤" : "👤"}
              </span>
            </motion.button>
            {activeTooltip === "profile" && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap">
                Your profile
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <motion.button
            onClick={toggleMenu}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="text-xl">{showMenu ? "✕" : "☰"}</span>
          </motion.button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {showMenu && (
        <motion.div
          className="md:hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg p-3 mb-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          <div className="grid grid-cols-4 gap-2">
            <NavItem 
              label="Add" 
              icon="➕" 
              onClick={() => navigateTo("input")} 
              active={currentView === "input"}
            />
            
            {purchaseExists() && (
              <>
                <NavItem 
                  label="Dashboard" 
                  icon="📊" 
                  onClick={() => navigateTo("dashboard")} 
                  active={currentView === "dashboard"}
                />
                
                <NavItem 
                  label="Goals" 
                  icon="🎯" 
                  onClick={() => navigateTo("goals")} 
                  active={currentView === "goals"}
                />
                
                <NavItem 
                  label="Insights" 
                  icon="📈" 
                  onClick={() => navigateTo("insights")} 
                  active={currentView === "insights"}
                />
                
                <NavItem 
                  label="Challenges" 
                  icon="🏆" 
                  onClick={() => navigateTo("challenges")} 
                  active={currentView === "challenges"}
                />
              </>
            )}
            
            <NavItem 
              label="Mindful Shop" 
              icon="🛍️" 
              onClick={() => navigateTo("shop")} 
              active={currentView === "shop"}
            />
            
            {/* New Quiz Button */}
            <NavItem 
              label="Quizzes" 
              icon="📝" 
              onClick={() => navigateTo("quiz-hub")} 
              active={currentView === "quiz-hub"}
            />
            
            <NavItem 
              label="Profile" 
              icon="👤" 
              onClick={showProfileModal} 
              active={false}
            />
            
            {/* Theme toggling based on current theme */}
            <NavItem
              label={colorTheme === "light" ? "Dark Mode" : "Light Mode"} 
              icon={colorTheme === "light" ? "🌙" : "☀️"} 
              onClick={() => {
                const newMode = colorTheme === "light" ? "dark" : "light";
                setColorTheme(newMode);
                localStorage.setItem("darkMode", newMode === "dark");
                document.documentElement.classList.toggle("dark", newMode === "dark");
              }} 
              active={false}
            />
            
            {/* Theme color options */}
            <NavItem 
              label="Mint" 
              icon="🌿" 
              onClick={() => setTheme("mint")} 
              active={theme === "mint"}
            />
            
            <NavItem 
              label="Pink" 
              icon="🌸" 
              onClick={() => setTheme("pink")} 
              active={theme === "pink"}
            />
            
            <NavItem 
              label="Lavender" 
              icon="💜" 
              onClick={() => setTheme("lavender")} 
              active={theme === "lavender"}
            />
          </div>
        </motion.div>
      )}
    </header>
  );
};

// Navigation item component for mobile menu
const NavItem = ({ label, icon, onClick, active }) => (
  <motion.button
    onClick={onClick}
    className={`flex flex-col items-center justify-center p-2 rounded-lg ${
      active ? "bg-blue-100 dark:bg-blue-900" : "hover:bg-gray-100 dark:hover:bg-gray-700"
    }`}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <span className="text-xl mb-1">{icon}</span>
    <span className="text-xs dark:text-white">{label}</span>
  </motion.button>
);

export default Header;
